/**
 * UserDashboardPage
 * [UC-036] User dashboard with personal projects and features
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { api } from '../core/api-client';
import { fmt, fmtMoney } from '../utils/format';

interface Project {
  projectId: number;
  name: string;
  description: string;
  status: string;
  creationDate: string;
}

interface DashboardData {
  projects: Project[];
  creditBalance: number;
}

export function UserDashboardPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    projects: [],
    creditBalance: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);

      // [UC-042] Load real dashboard data from API. Default page size is
      // 20; bump to the backend max so the list reflects everything the
      // user owns, not just the first page.
      const [projectsResponse, userResponse] = await Promise.all([
        api.get('/projects', { params: { limit: 100 } }),
        api.get('/users/me')
      ]);

      const projects = ((projectsResponse as any).items || [])
        .map((project: any) => ({
          projectId: project.project_id || project.projectId,
          name: project.name,
          description: project.description,
          status: project.status,
          creationDate: project.creation_date || project.creationDate
        }))
        .sort((a: { creationDate: string }, b: { creationDate: string }) =>
          (b.creationDate || '').localeCompare(a.creationDate || '')
        );

      // /users/me returns PaginatedResponse[UserResponse]. Pydantic serializes
      // UserResponse with camelCase aliases (creditBalance, etc.) — read those
      // and tolerate both shapes to stay resilient to future endpoint changes.
      const usersPayload = userResponse as any;
      const userData =
        (Array.isArray(usersPayload?.items) && usersPayload.items[0]) ||
        usersPayload ||
        {};
      const credits = Number(
        userData.creditBalance ?? userData.credit_balance ?? 0,
      );

      setDashboardData({
        projects,
        creditBalance: Number.isFinite(credits) ? credits : 0,
      });

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error(t('userDashboard.errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'Delivered':
        return 'bg-green-100 text-green-800';
      case 'InDevelopment':
        return 'bg-blue-100 text-blue-800';
      case 'ForTesting':
        return 'bg-yellow-100 text-yellow-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-6 flex items-center space-x-3 shadow-lg">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
          <span className="text-gray-900">{t('userDashboard.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
      {/* Page header — nothing redundant with the sidebar. Just a clean
          "Projects" label, the credit pill, and the primary create action. */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl lg:text-3xl font-bold text-[var(--text-primary)]">
          {t('nav.projects', 'Projects')}
        </h1>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => navigate('/user-credit-purchase')}
            title={t('userDashboard.actions.purchaseCredits', 'Purchase credits')}
            className="inline-flex items-center gap-2 pl-2.5 pr-3.5 py-1.5 rounded-xl border border-[var(--border-subtle)] bg-[var(--accent-soft)] hover:border-[var(--accent-primary)] transition-colors"
          >
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-[var(--accent-primary)] text-[var(--accent-text)] text-xs font-semibold">
              €
            </span>
            <span className="text-sm font-semibold text-[var(--text-primary)] tabular-nums">
              {fmt(dashboardData.creditBalance)}
            </span>
            <span className="text-xs text-[var(--text-muted)]">
              {t('userDashboard.credits', 'credits')}
            </span>
          </button>

          <button
            onClick={() => navigate('/user-project-create')}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-sm font-medium transition-colors shadow-sm"
          >
            <span className="text-base leading-none">+</span>
            {t('userDashboard.buttons.createProject', 'New project')}
          </button>
        </div>
      </div>

      {/* Projects list */}
      {dashboardData.projects.length === 0 ? (
        <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-10 text-center">
          <p className="text-base text-[var(--text-secondary)] mb-5">
            {t('userDashboard.empty.noProjects', "You don't have any projects yet.")}
          </p>
          <button
            onClick={() => navigate('/user-project-create')}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-sm font-medium transition-colors shadow-sm"
          >
            {t('userDashboard.buttons.createFirstProject', 'Create your first project')}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {dashboardData.projects.map((project) => (
            <div
              key={project.projectId}
              onClick={() => navigate('/user-project-detail', { state: { projectId: project.projectId } })}
              className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] hover:border-[var(--border-default)] shadow-sm hover:shadow-md transition-all cursor-pointer p-5"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-[var(--text-primary)] mb-1 truncate">
                    {project.name}
                  </h3>
                  {project.description && (
                    <p className="text-sm text-[var(--text-secondary)] mb-2 line-clamp-2">
                      {project.description}
                    </p>
                  )}
                  <p className="text-xs text-[var(--text-muted)]">
                    {t('userDashboard.project.created', 'Created')}: {formatDate(project.creationDate)}
                  </p>
                </div>
                <span className={`flex-shrink-0 inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(project.status)}`}>
                  {t(`userDashboard.project.status.${project.status}`, project.status)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default UserDashboardPage;
